import { Injectable, inject, DOCUMENT } from '@angular/core';
import { HeadService, NHead } from '@df/core/meta/head/head.service';
import { NDfModuleImage } from './df-module-image.interface';

/**
 * In SSR scenarios, a preload `<link>` element is generated for priority images.
 * Having a large number of preload tags may negatively affect the performance,
 * so we warn developers (by throwing an error) if the number of preloaded images
 * is above a certain threshold. This const specifies this threshold.
 */
export const DEFAULT_PRELOADED_IMAGES_LIMIT = 5;

/**
 * @description Contains the logic needed to track and add preload link tags to the `<head>` tag. It
 * will also track what images have already had preload link tags added so as to not duplicate link
 * tags.
 *
 * In dev mode this service will validate that the number of preloaded images does not exceed the
 * configured default preloaded images limit: {@link DEFAULT_PRELOADED_IMAGES_LIMIT}.
 */
@Injectable({ providedIn: 'root' })
export class PreloadLinkCreator {
    private readonly preloadedImages = inject(NDfModuleImage.PRELOADED_IMAGES);
    private readonly document = inject(DOCUMENT);
    private readonly headService = inject(HeadService);

    /**
     * @description Add a preload `<link>` to the `<head>` of the `index.html` that is served from the
     * server while using Angular Universal and SSR to kick off image loads for high priority images.
     *
     * The `sizes` (passed in from the user) and `srcset` (parsed and formatted from `ngSrcset`)
     * properties used to set the corresponding attributes, `imagesizes` and `imagesrcset`
     * respectively, on the preload `<link>` tag so that the correctly sized image is preloaded from
     * the CDN.
     *
     * {@link https://web.dev/preload-responsive-images/#imagesrcset-and-imagesizes}
     *
     * @param src The original src of the image that is set on the `ngSrc` input.
     * @param srcset The parsed and formatted srcset created from the `ngSrcset` input
     * @param sizes The value of the `sizes` attribute passed in to the `<img>` tag
     */
    createPreloadLinkTag(src?: string, srcset?: string, sizes?: string): void {
        if (!src || this.preloadedImages.has(src)) {
            return;
        }

        this.preloadedImages.add(src);

        const attributes: NHead.ILinkConfig['attributes'] = {
            as: 'image',
            href: src,
            rel: 'preload',
            fetchpriority: 'high'
        };

        if (sizes) {
            attributes['imageSizes'] = sizes;
        }

        if (srcset) {
            attributes['imageSrcset'] = srcset;
        }

        this.headService.add({
            element: 'link',
            attributes,
            ssr: true
        });
    }
}
